import React from "react";
import { Box, Typography } from "@mui/material";

/**
 * Glass-style placeholder with a soft pulse (“blink”) for image/video loading.
 */
export default function GlassMediaLoader({
  mediaType = "image",
  mode = "generating",
}) {
  const kind = String(mediaType ?? "image").toLowerCase();
  const isVideo = kind === "video";
  const isContent = kind === "content";

  const label =
    mode === "loading"
      ? isVideo
        ? "Loading video"
        : isContent
          ? "Loading script"
          : "Loading image"
      : isVideo
        ? "Generating video"
        : isContent
          ? "Generating script"
          : "Generating image";

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: isVideo ? 520 : 400,
        aspectRatio: isVideo ? "16 / 9" : "4 / 3",
        minHeight: 200,
        position: "relative",
        borderRadius: 3,
        overflow: "hidden",
        isolation: "isolate",
        background:
          "linear-gradient(145deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.03) 45%, rgba(120,160,255,0.06) 100%)",
        backdropFilter: "blur(22px) saturate(140%)",
        WebkitBackdropFilter: "blur(22px) saturate(140%)",
        border: "1px solid rgba(255,255,255,0.22)",
        boxShadow:
          "0 12px 40px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.25)",
        animation: "glassBlink 1.65s ease-in-out infinite",
        "@keyframes glassBlink": {
          "0%, 100%": {
            opacity: 0.65,
            filter: "brightness(0.92)",
            boxShadow:
              "0 8px 28px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.12)",
          },
          "50%": {
            opacity: 1,
            filter: "brightness(1.08)",
            boxShadow:
              "0 16px 48px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.35)",
          },
        },
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.14) 1px, transparent 1px)",
          backgroundSize: "16px 16px",
          opacity: 0.55,
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.35) 35%, black 100%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.35) 35%, black 100%)",
          pointerEvents: "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          inset: "-40% -60%",
          background:
            "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.14) 50%, transparent 60%)",
          animation: "glassShimmer 2.4s ease-in-out infinite",
          "@keyframes glassShimmer": {
            "0%": { transform: "translateX(-12%) skewX(-12deg)" },
            "100%": { transform: "translateX(18%) skewX(-12deg)" },
          },
          pointerEvents: "none",
        },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          position: "absolute",
          top: 18,
          left: 20,
          zIndex: 1,
          fontWeight: 500,
          letterSpacing: "0.02em",
          color: "rgba(255,255,255,0.92)",
          textShadow: "0 1px 12px rgba(0,0,0,0.45)",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
